/*
 * LAVIRENA product purchase (2026-10-23)
 * One file for everything around choosing a size and buying on product pages. It replaces
 *   lavirena-reference-size-nav-fix.js, elvessia-purchase-gate.js and lavirena-purchase-fix.js.
 *
 *   A. Styles for the bottom purchase bar and the mobile payment row / SN
 *   B. Size buttons with the US / EU switch (drawn on top of Shopify's own size radios)
 *   C. Purchase bar: SELECT A SIZE until a size is picked, then ADD TO CART / BUY NOW
 *   D. Bar summary (thumbnail, price, shipping note) and hiding the theme's own sticky bar
 *   E. Mobile only: payment row after the picker, SN at the end, compare-at price, gallery height
 *   F. Shipping country wording
 *
 * Every product uses one "Size" option whose values look like "US 6" or "EU 36".
 * One MutationObserver feeds all parts; each part only writes when something actually changed,
 * so the page goes quiet once it has settled.
 */
(() => {
  if (window.__lvProductPurchase) return;
  window.__lvProductPurchase = true;

  /* ---------- shared helpers ---------- */
  const mq = window.matchMedia('(max-width: 749px)');
  const isMobile = () => mq.matches;
  const optionName = (input) => String(input?.dataset?.optionName || '').trim().toLowerCase();
  const isSize = (input) => optionName(input) === 'size';

  let productData = null;
  let productPromise = null;
  const loadProduct = () => {
    if (productPromise) return productPromise;
    const path = location.pathname.replace(/\/$/, '');
    if (!/\/products\/[^/]+$/i.test(path)) return Promise.resolve(null);
    productPromise = fetch(`${path}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { productData = data; return data; })
      .catch(() => null);
    return productPromise;
  };

  const formatMoney = (cents) => {
    if (typeof cents !== 'number') return '';
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  // Runs fn once on the next frame, however many times it is asked for before then.
  const perFrame = (fn) => {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; fn(); });
    };
  };

  const productForm = () => document.querySelector('.buy-buttons-block')
    ?.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
  const formVariantId = () => String(productForm()?.querySelector('input[name="id"]')?.value || '').trim();

  /* ---------- A. styles ---------- */
  const STYLE_ID = 'lv-product-purchase-styles';
  const ensureStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .buy-buttons-block.elvessia-inline-source-hidden {
        position:absolute!important;
        width:1px!important;
        height:1px!important;
        min-width:0!important;
        min-height:0!important;
        margin:0!important;
        padding:0!important;
        overflow:hidden!important;
        opacity:0!important;
        pointer-events:none!important;
        clip-path:inset(50%)!important;
      }
      .elvessia-purchase-bar {
        position:fixed;
        left:0;
        right:0;
        bottom:0;
        z-index:9999;
        display:flex;
        gap:18px;
        align-items:center;
        padding:10px max(16px,calc(50vw - 620px));
        padding-bottom:max(10px,env(safe-area-inset-bottom));
        background:rgba(255,255,255,.98);
        border-top:1px solid rgba(0,0,0,.14);
        box-shadow:0 -4px 16px rgba(0,0,0,.06);
      }
      .elvessia-purchase-bar__summary { min-width:0; flex:1; font-size:13px; line-height:1.25; }
      .elvessia-purchase-bar__title { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-weight:650; }
      .elvessia-purchase-bar__meta { color:rgba(0,0,0,.62); margin-top:2px; }
      .elvessia-purchase-bar__actions { width:min(540px,46vw); min-width:360px; }
      .elvessia-purchase-bar__select,
      .elvessia-purchase-bar__add,
      .elvessia-purchase-bar__buy-now { width:100%; min-height:54px; padding:0 18px; font:inherit; font-weight:700; cursor:pointer; text-transform:uppercase; }
      .elvessia-purchase-bar__select,
      .elvessia-purchase-bar__add { border:1px solid #0d0d0d; background:#0d0d0d; color:#fff; }
      .elvessia-purchase-bar__buy-now { border:1px solid #0d0d0d; background:#fff; color:#0d0d0d; }
      .elvessia-purchase-bar__purchase { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:10px; }
      .elvessia-purchase-bar__select[hidden], .elvessia-purchase-bar__purchase[hidden] { display:none!important; }
      @media(max-width:749px){
        .elvessia-purchase-bar { gap:10px; padding-inline:12px; }
        .elvessia-purchase-bar__summary { display:none; }
        .elvessia-purchase-bar__actions { width:100%; min-width:0; }
        .elvessia-purchase-bar__select,
        .elvessia-purchase-bar__add,
        .elvessia-purchase-bar__buy-now { min-height:48px; padding-inline:10px; font-size:13px; }
      }

      main[data-template='product'] .l1-vivaia-purchase-actions,
      main[data-template='product'] .sticky-add-to-cart__bar {
        display: none !important;
      }

      /* Bottom purchase bar: VIVAIA-style layout only. */
      .elvessia-purchase-bar {
        position: fixed !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        max-width: 100vw !important;
        min-height: 122px !important;
        display: flex !important;
        align-items: center !important;
        gap: 32px !important;
        padding: 14px max(40px, calc((100vw - 1780px) / 2)) 36px !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
        background: #fff !important;
        border-top: 1px solid rgba(0,0,0,.10) !important;
        box-shadow: 0 -2px 10px rgba(0,0,0,.04) !important;
        z-index: 2147483000 !important;
      }

      /* Keep the product purchase bar out of the cart drawer/checkout path. */
      body:has(#cart-drawer[open]) .elvessia-purchase-bar,
      body:has(#cart-drawer dialog[open]) .elvessia-purchase-bar {
        display: none !important;
      }

      .elvessia-purchase-bar,
      .elvessia-purchase-bar * {
        box-sizing: border-box !important;
      }

      .elvessia-purchase-bar__summary {
        display: flex !important;
        align-items: center !important;
        gap: 16px !important;
        min-width: 0 !important;
        flex: 1 1 auto !important;
      }

      .lv-fixed-summary__thumb {
        width: 74px !important;
        height: 74px !important;
        flex: 0 0 74px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        overflow: hidden !important;
        padding: 4px !important;
        background: #fff !important;
        border: 1px solid #e5e5e5 !important;
        border-radius: 3px !important;
      }

      .lv-fixed-summary__thumb img {
        width: 100% !important;
        height: 100% !important;
        display: block !important;
        object-fit: contain !important;
      }

      .lv-fixed-summary__content {
        min-width: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        gap: 9px !important;
        color: #111 !important;
        font-family: var(--font-paragraph--family) !important;
      }

      .lv-fixed-summary__price {
        font-size: 18px !important;
        line-height: 1.15 !important;
        font-weight: 700 !important;
        white-space: nowrap !important;
      }

      .lv-fixed-summary__shipping {
        font-size: 14px !important;
        line-height: 1.35 !important;
        font-weight: 400 !important;
        color: #333 !important;
        white-space: nowrap !important;
      }

      .lv-fixed-summary__shipping::before {
        content: '◇';
        display: inline-block;
        margin-right: 7px;
        color: #8b4d18;
        font-size: 17px;
        line-height: 1;
        vertical-align: -1px;
      }

      .elvessia-purchase-bar__actions {
        width: min(620px, 42vw) !important;
        min-width: 520px !important;
        flex: 0 0 min(620px, 42vw) !important;
        margin-left: auto !important;
      }

      .elvessia-purchase-bar__purchase {
        display: grid !important;
        grid-template-columns: minmax(0,1fr) minmax(0,1fr) !important;
        gap: 14px !important;
        width: 100% !important;
      }

      .elvessia-purchase-bar__purchase[hidden],
      .elvessia-purchase-bar__select[hidden] {
        display: none !important;
      }

      .elvessia-purchase-bar__select,
      .elvessia-purchase-bar__add,
      .elvessia-purchase-bar__buy-now {
        width: 100% !important;
        min-height: 64px !important;
        height: 64px !important;
        margin: 0 !important;
        padding: 0 22px !important;
        border-radius: 4px !important;
        font-family: var(--font-paragraph--family) !important;
        font-size: 17px !important;
        line-height: 1 !important;
        font-weight: 700 !important;
        text-transform: uppercase !important;
      }

      .elvessia-purchase-bar__select,
      .elvessia-purchase-bar__add {
        border: 1px solid #111 !important;
        background: #111 !important;
        color: #fff !important;
      }

      .elvessia-purchase-bar__buy-now {
        border: 2px solid #111 !important;
        background: #fff !important;
        color: #111 !important;
      }

      main[data-template='product'] {
        padding-bottom: 142px !important;
      }

      @media(max-width: 749px) {
        /* ELVESSIA mobile sticky purchase layout v1: VIVAIA-like price + primary action. */
        .elvessia-purchase-bar {
          min-height: 116px !important;
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 7px !important;
          padding: 8px 12px max(16px, env(safe-area-inset-bottom)) !important;
        }
        .elvessia-purchase-bar__summary {
          display: block !important;
          width: 100% !important;
          flex: 0 0 auto !important;
        }
        .elvessia-purchase-bar__summary .lv-fixed-summary__thumb,
        .elvessia-purchase-bar__summary .lv-fixed-summary__shipping {
          display: none !important;
        }
        .elvessia-purchase-bar__summary .lv-fixed-summary__content {
          display: block !important;
        }
        .elvessia-purchase-bar__summary .lv-fixed-summary__price {
          font-size: 19px !important;
          line-height: 1.15 !important;
          font-weight: 700 !important;
        }
        .elvessia-purchase-bar__actions {
          width: 100% !important;
          min-width: 0 !important;
          flex: 1 1 auto !important;
        }
        .elvessia-purchase-bar__purchase {
          gap: 8px !important;
        }
        .elvessia-purchase-bar__select,
        .elvessia-purchase-bar__add,
        .elvessia-purchase-bar__buy-now {
          min-height: 52px !important;
          height: 52px !important;
          padding-inline: 10px !important;
          font-size: 14px !important;
        }
        main[data-template='product'] {
          padding-bottom: 132px !important;
        }
      }

      @media(max-width:749px){
        /* Remove the old pre-size trust/meta stack on mobile only. */
        .lavirena-vivaia-variant-picker > .lv-audrey-meta{
          display:none!important;
        }

        /* VIVAIA order after size guide: payment -> ship/returns/ETA -> highlights -> details/care -> SN. */
        .elvessia-mobile-payment-row{
          display:flex;
          align-items:center;
          flex-wrap:wrap;
          gap:7px 8px;
          width:100%;
          margin:0 0 16px;
          padding:0;
          color:rgb(var(--color-foreground-rgb));
          font-family:var(--font-paragraph--family);
          font-size:13px;
          line-height:1.35;
        }
        .elvessia-mobile-payment-row__label{
          white-space:nowrap;
        }
        .elvessia-mobile-payment-row .lv-audrey-payment-icon{
          display:inline-flex!important;
          align-items:center!important;
          justify-content:center!important;
          height:24px!important;
        }
        .elvessia-mobile-payment-row .lv-audrey-payment-icon svg{
          display:block!important;
          width:auto!important;
          height:24px!important;
        }

        /* Use the existing Ship to block in its natural VIVAIA-style location. */
        .lv-vivaia-info > .lv-vivaia-ship{
          display:block!important;
          margin-top:0!important;
        }

        /* SN belongs after details/care on mobile, not in the buying decision area. */
        .elvessia-mobile-sn{
          display:block;
          margin:14px 0 0;
          padding:14px 0 0;
          border-top:1px solid rgb(var(--color-foreground-rgb)/.12);
          color:rgb(var(--color-foreground-rgb)/.48);
          font-family:var(--font-paragraph--family);
          font-size:13px;
          line-height:1.4;
        }

        /* Old v1 nodes, if present during theme-editor hot reload, must never display. */
        .elvessia-mobile-tax-note,
        .elvessia-mobile-trust-block{
          display:none!important;
        }
      }
    `;
    document.head.appendChild(style);
  };

  /* ---------- B. size buttons with US / EU switch ---------- */
  const sizePicker = (() => {
    let lastFieldset = null;
    let lastSignature = '';
    let preferredUnit = 'US';

    const active = () => !!document.querySelector('[data-lavirena-template-one-size-system], [data-lavirena-reference-fixed-size-system]');

    const parseSize = (raw) => {
      const value = String(raw || '').trim();
      const us = value.match(/\bUS\s*([\d.]+(?:\s*-\s*[\d.]+)?)/i);
      const eu = value.match(/\bEU\s*([\d.]+(?:\s*-\s*[\d.]+)?)/i);
      const entries = [];
      if (us) entries.push({ system: 'US', label: us[1].replace(/\s/g, '') });
      if (eu) entries.push({ system: 'EU', label: eu[1].replace(/\s/g, '') });
      if (!entries.length) {
        const number = value.replace(/\s/g, '');
        if (/^\d+(?:\.\d+)?$/.test(number)) entries.push({ system: Number(number) >= 30 ? 'EU' : 'US', label: number });
        else if (value) entries.push({ system: 'OTHER', label: value });
      }
      return entries;
    };

    const nativeSizes = () => {
      const main = document.querySelector('#MainContent') || document;
      for (const fieldset of main.querySelectorAll('fieldset')) {
        const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
        if (!inputs.length) continue;
        const sizeInputs = inputs.filter(isSize);
        if (sizeInputs.length || /\bsize\b/i.test(fieldset.querySelector('legend')?.textContent || '')) {
          return { fieldset, inputs: sizeInputs.length ? sizeInputs : inputs };
        }
      }
      return null;
    };

    const unavailable = (input) => input.disabled
      || input.getAttribute('aria-disabled') === 'true'
      || input.dataset.optionAvailable === 'false';

    const numericSort = (a, b) => {
      const an = Number.parseFloat(a.label);
      const bn = Number.parseFloat(b.label);
      if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;
      return String(a.label).localeCompare(String(b.label), undefined, { numeric: true });
    };

    const buildGroups = (items) => {
      const groups = { US: [], EU: [], OTHER: [] };
      const seen = new Set();
      for (const input of items) {
        for (const parsed of parseSize(input.value)) {
          const key = `${parsed.system}:${parsed.label}`;
          if (seen.has(key)) continue;
          seen.add(key);
          groups[parsed.system].push({ ...parsed, input });
        }
      }
      groups.US.sort(numericSort);
      groups.EU.sort(numericSort);
      groups.OTHER.sort(numericSort);
      return groups;
    };

    // A size counts as picked only once the purchase bar (part C) has confirmed it.
    const isSelected = (input) => {
      const wrap = input.closest('.lavirena-vivaia-variant-picker');
      return input.checked && !unavailable(input)
        && wrap?.dataset.elvessiaSizeChosen === 'true'
        && wrap.dataset.elvessiaSizeValue === input.value;
    };

    const makeButton = (entry) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lv-ref-size-slot';
      button.dataset.nativeSize = entry.input.value;
      button.dataset.sizeSystem = entry.system;
      button.textContent = entry.label;
      const isUnavailable = unavailable(entry.input);
      const selected = isSelected(entry.input);
      button.disabled = isUnavailable;
      button.classList.toggle('is-selected', selected);
      button.classList.toggle('is-unavailable', isUnavailable);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.setAttribute('aria-label', `${entry.system === 'OTHER' ? '' : `${entry.system} `}${entry.label}${isUnavailable ? ' unavailable' : ''}`);
      button.addEventListener('click', () => {
        if (button.disabled) return;
        // Section morphs may replace the native inputs; use the live one.
        const input = nativeSizes()?.inputs.find((item) => item.value === entry.input.value);
        if (!input || !input.isConnected || unavailable(input)) return;
        input.click();
        schedule();
      });
      return button;
    };

    const makeGroup = (system, entries) => {
      if (!entries.length) return null;
      const group = document.createElement('div');
      group.className = 'lv-ref-size-group';
      group.dataset.sizeSystem = system;
      const grid = document.createElement('div');
      grid.className = 'lv-ref-size-grid';
      const frag = document.createDocumentFragment();
      entries.forEach((entry) => frag.appendChild(makeButton(entry)));
      grid.appendChild(frag);
      group.append(grid);
      return group;
    };

    const selectedTextForSystem = (items, system) => {
      const selected = items.find(isSelected);
      if (!selected) return 'Select a size';
      const match = parseSize(selected.value).find((entry) => entry.system === system);
      return match ? match.label : 'Select a size';
    };

    const render = () => {
      if (!active()) return;
      const native = nativeSizes();
      if (!native || !native.inputs.length) return;

      const { fieldset } = native;
      const wrap = fieldset.closest('.lavirena-vivaia-variant-picker');
      const items = [...new Map(native.inputs.map((input) => [input.value, input])).values()];
      const signature = [
        preferredUnit,
        wrap?.dataset.elvessiaSizeChosen,
        wrap?.dataset.elvessiaSizeValue,
        ...items.map((i) => `${i.value}:${i.checked}:${i.disabled}:${i.getAttribute('aria-disabled')}:${i.dataset.optionAvailable}`),
      ].join('|');
      if (fieldset === lastFieldset && signature === lastSignature && fieldset.querySelector('.lv-ref-size-picker')) return;
      lastFieldset = fieldset;
      lastSignature = signature;

      fieldset.classList.add('lv-ref-size-fieldset');
      for (const input of native.inputs) input.closest('label')?.classList.add('lv-ref-native-size');

      let shell = fieldset.querySelector('.lv-ref-size-picker');
      if (!shell) {
        shell = document.createElement('div');
        shell.className = 'lv-ref-size-picker';
        shell.innerHTML = '<div class="lv-ref-size-heading"><span class="lv-ref-size-current"></span></div><div class="lv-ref-size-groups"></div>';
        fieldset.append(shell);
      }

      const heading = shell.querySelector('.lv-ref-size-heading');
      const current = shell.querySelector('.lv-ref-size-current');
      if (current) current.textContent = `Size: ${selectedTextForSystem(items, preferredUnit)}`;

      let select = heading?.querySelector('.lv-ref-size-unit');
      if (!select && heading) {
        select = document.createElement('select');
        select.className = 'lv-ref-size-unit';
        select.setAttribute('aria-label', 'Size system');
        select.innerHTML = '<option value="US">US</option><option value="EU">EU</option>';
        select.addEventListener('change', (event) => {
          // Display unit only, not a Shopify product option.
          event.stopPropagation();
          preferredUnit = event.target.value === 'EU' ? 'EU' : 'US';
          const picked = nativeSizes()?.inputs.find(isSelected);
          if (picked && !parseSize(picked.value).some((entry) => entry.system === preferredUnit)) {
            // A US size is not an EU size: switching units clears the choice.
            purchaseBar.clear();
          }
          lastSignature = '';
          schedule();
        });
        heading.appendChild(select);
      }
      if (select) select.value = preferredUnit;

      const groupsHost = shell.querySelector('.lv-ref-size-groups');
      if (!groupsHost) return;
      const groups = buildGroups(items);
      const frag = document.createDocumentFragment();
      if (groups.US.length || groups.EU.length) {
        const unit = groups[preferredUnit].length ? preferredUnit : (groups.US.length ? 'US' : 'EU');
        preferredUnit = unit;
        const group = makeGroup(unit, groups[unit]);
        if (group) frag.appendChild(group);
      } else {
        const group = makeGroup('OTHER', groups.OTHER);
        if (group) frag.appendChild(group);
      }
      groupsHost.replaceChildren(frag);
    };

    const schedule = perFrame(render);
    const refresh = () => { lastSignature = ''; schedule(); };
    const relevant = (record) => record.target instanceof Element
      && (record.target.closest('variant-picker')
        || record.target.matches('[data-lavirena-template-one-size-system], [data-lavirena-reference-fixed-size-system]'));
    return { schedule, refresh, relevant };
  })();

  /* ---------- C. purchase bar ---------- */
  const purchaseBar = (() => {
    let sizeChosen = false;
    let chosenSize = '';
    let chosenColor = '';
    let selectionRevision = 0;

    // Mobile resolves the variant from the product JSON; desktop trusts the radio's variant id.
    const mobileData = () => (isMobile() ? productData : null);
    const sizeInputs = () => [...document.querySelectorAll('variant-picker input[type="radio"]')].filter(isSize);
    const selectedSize = () => sizeInputs().find((input) => input.checked);
    const currentColor = () => [...document.querySelectorAll('main variant-picker input[type="radio"]:checked')]
      .find((input) => optionName(input) === 'color')?.value || '';
    const sizeAvailable = (input) => !!input && !input.disabled
      && input.getAttribute('aria-disabled') !== 'true' && input.dataset.optionAvailable !== 'false';

    const sizeVariant = () => {
      const data = mobileData();
      if (!data || !sizeChosen || chosenColor !== currentColor()) return null;
      const names = (data.options || []).map((option) => String(typeof option === 'string' ? option : option.name).trim().toLowerCase());
      const sizeIndex = names.indexOf('size');
      const colorIndex = names.indexOf('color');
      if (sizeIndex < 0) return null;
      return data.variants.find((variant) => variant.options[sizeIndex] === chosenSize
        && (colorIndex < 0 || variant.options[colorIndex] === chosenColor)) || null;
    };

    const hasSize = () => {
      const input = selectedSize();
      const formVariant = formVariantId();
      if (mobileData()) {
        const variant = sizeVariant();
        return !!variant?.available && input?.value === chosenSize && String(variant.id) === formVariant;
      }
      return sizeChosen && sizeAvailable(input) && input.value === chosenSize
        && !!formVariant && String(input.dataset.variantId || '') === formVariant;
    };

    const ensureBar = () => {
      let bar = document.querySelector('.elvessia-purchase-bar');
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'elvessia-purchase-bar';
        document.body.appendChild(bar);
      }
      if (!bar.querySelector('.elvessia-purchase-bar__select')) {
        bar.innerHTML = '<div class="elvessia-purchase-bar__summary"><div class="elvessia-purchase-bar__title"></div><div class="elvessia-purchase-bar__meta"></div></div><div class="elvessia-purchase-bar__actions"><button type="button" class="elvessia-purchase-bar__select">SELECT A SIZE</button><div class="elvessia-purchase-bar__purchase" hidden><button type="button" class="elvessia-purchase-bar__add">ADD TO CART</button><button type="button" class="elvessia-purchase-bar__buy-now">BUY NOW</button></div></div>';
      }
      const title = bar.querySelector('.elvessia-purchase-bar__title');
      if (title && !title.textContent) title.textContent = document.querySelector('h1')?.textContent?.trim() || 'LAVIRENA';
      return bar;
    };

    const scrollToSize = () => {
      const target = document.querySelector('.lv-ref-size-picker')
        || sizeInputs()[0]?.closest('fieldset,.variant-option')
        || document.querySelector('.lavirena-size-guide-inline')
        || document.querySelector('.lavirena-vivaia-variant-picker');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // The theme pre-checks a size; nothing counts as chosen until the shopper picks one.
    const clearThemeDefaultSize = () => {
      if (sizeChosen) return;
      sizeInputs().forEach((input) => { if (input.checked) input.checked = false; });
    };

    const restoreChosenSize = () => {
      if (!sizeChosen || !chosenSize) return;
      const input = sizeInputs().find((item) => String(item.value) === String(chosenSize));
      const data = mobileData();
      if ((data && chosenColor !== currentColor()) || (data ? !sizeVariant()?.available : !sizeAvailable(input))) {
        sizeChosen = false;
        chosenSize = '';
        clearThemeDefaultSize();
        return;
      }
      if (input && !input.checked) input.checked = true;
    };

    const sync = () => {
      clearThemeDefaultSize();
      restoreChosenSize();

      // Keep a late section response from putting a previous variant back over the shopper's choice.
      const variant = sizeVariant();
      if (isMobile() && variant?.available) {
        const input = selectedSize();
        if (input?.value === chosenSize) {
          input.dataset.variantId = String(variant.id);
          const idInput = productForm()?.querySelector('input[name="id"]');
          if (idInput && idInput.value !== String(variant.id)) idInput.value = String(variant.id);
        }
      }

      const buy = document.querySelector('.buy-buttons-block');
      if (buy) {
        buy.classList.add('elvessia-inline-source-hidden');
        const flag = hasSize() ? 'true' : 'false';
        if (buy.dataset.elvessiaSizeSelected !== flag) buy.dataset.elvessiaSizeSelected = flag;
      }

      const bar = ensureBar();
      const selected = selectedSize();
      const locked = !hasSize();
      const wrap = selected?.closest('.lavirena-vivaia-variant-picker') || document.querySelector('.lavirena-vivaia-variant-picker');
      if (wrap) {
        const chosen = locked ? 'false' : 'true';
        const value = locked ? '' : (selected?.value || '');
        if (wrap.dataset.elvessiaSizeChosen !== chosen || wrap.dataset.elvessiaSizeValue !== value) {
          wrap.dataset.elvessiaSizeChosen = chosen;
          wrap.dataset.elvessiaSizeValue = value;
          sizePicker.refresh();
        }
      }
      const meta = bar.querySelector('.elvessia-purchase-bar__meta');
      const selectButton = bar.querySelector('.elvessia-purchase-bar__select');
      const purchaseButtons = bar.querySelector('.elvessia-purchase-bar__purchase');
      const metaText = locked ? 'Choose a size to continue' : `Size ${selected?.value || chosenSize}`;
      if (meta && meta.textContent !== metaText) meta.textContent = metaText;
      if (selectButton && selectButton.hidden !== !locked) selectButton.hidden = !locked;
      if (purchaseButtons && purchaseButtons.hidden !== locked) purchaseButtons.hidden = locked;
    };

    const syncNextFrame = perFrame(sync);
    let debounceTimer = 0;
    const syncSoon = () => { clearTimeout(debounceTimer); debounceTimer = setTimeout(sync, 40); };

    const commitSize = (input) => {
      sizeChosen = true;
      chosenSize = input.value;
      chosenColor = currentColor();
      if (!isMobile()) { syncNextFrame(); return; }
      const revision = ++selectionRevision;
      const commit = () => {
        if (revision !== selectionRevision || chosenColor !== currentColor()) return;
        const variant = sizeVariant();
        if (!variant?.available) { sync(); return; }
        const liveInput = sizeInputs().find((item) => item.value === chosenSize);
        if (!liveInput) return;
        liveInput.checked = true;
        liveInput.dataset.variantId = String(variant.id);
        const idInput = productForm()?.querySelector('input[name="id"]');
        if (idInput) idInput.value = String(variant.id);
        sync();
      };
      if (productData) commit();
      else loadProduct().then(commit);
    };

    const clear = () => {
      selectionRevision += 1;
      sizeChosen = false;
      chosenSize = '';
      sync();
    };

    const sizeInputFromClick = (target) => {
      const direct = target.closest?.('variant-picker input[type="radio"]');
      if (direct && isSize(direct)) return direct;
      const input = target.closest?.('variant-picker label')?.querySelector('input[type="radio"]');
      return input && isSize(input) ? input : null;
    };

    document.addEventListener('change', (event) => {
      const input = event.target.closest?.('variant-picker input[type="radio"]');
      if (!input) return;
      if (optionName(input) === 'color') {
        if (!isMobile()) return;
        selectionRevision += 1;
        sizeChosen = false;
        chosenSize = '';
        chosenColor = input.value;
        sync();
        return;
      }
      if (!isSize(input) || input.disabled) return;
      // Point the cart form at the picked size right away instead of waiting for the section reload.
      const variantId = String(input.dataset.variantId || '');
      if (variantId) {
        document.querySelectorAll('.buy-buttons-block form input[name="id"], product-form-component form input[name="id"]')
          .forEach((idInput) => { if (idInput.value !== variantId) idInput.value = variantId; });
      }
      commitSize(input);
    }, true);

    document.addEventListener('submit', (event) => {
      if (event.target !== productForm() || hasSize()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      sync();
      scrollToSize();
    }, true);

    document.addEventListener('click', (event) => {
      const clickedSize = sizeInputFromClick(event.target);
      if (clickedSize && !clickedSize.disabled) commitSize(clickedSize);

      if (event.target.closest?.('.elvessia-purchase-bar__select')) {
        event.preventDefault();
        scrollToSize();
        return;
      }

      if (event.target.closest?.('.elvessia-purchase-bar__add')) {
        event.preventDefault();
        if (!hasSize()) { scrollToSize(); return; }
        const form = productForm();
        const submitButton = form?.querySelector('button[name="add"]:not([disabled])');
        if (form && submitButton) form.requestSubmit(submitButton);
        return;
      }

      if (event.target.closest?.('.elvessia-purchase-bar__buy-now')) {
        event.preventDefault();
        if (!hasSize()) { scrollToSize(); return; }
        const variantId = formVariantId() || String(selectedSize()?.dataset.variantId || '').trim();
        if (!variantId) { scrollToSize(); return; }
        const root = window.Shopify?.routes?.root || '/';
        const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
        window.location.assign(`${normalizedRoot}cart/${encodeURIComponent(variantId)}:1`);
      }
    }, true);

    return { sync, syncSoon, ensureBar, clear };
  })();

  /* ---------- D. bar summary + theme sticky bar ---------- */
  const summary = (() => {
    const selectedVariantId = () => {
      const checked = [...document.querySelectorAll('variant-picker input[data-variant-id]:checked')];
      const size = checked.find(isSize);
      return String(size?.dataset.variantId || checked[checked.length - 1]?.dataset.variantId || '');
    };

    const productImage = (data, variant) => {
      const featured = variant?.featured_image;
      if (typeof featured === 'string') return featured;
      if (featured?.src) return featured.src;
      if (typeof data?.featured_image === 'string') return data.featured_image;
      if (data?.featured_image?.src) return data.featured_image.src;
      const img = document.querySelector('product-media-gallery img, media-gallery img, .product-media-gallery img, [data-product-media] img');
      return img?.currentSrc || img?.src || '';
    };

    const hideThemeBars = () => {
      document.querySelectorAll('.l1-vivaia-purchase-actions').forEach((node) => node.remove());
      document.querySelectorAll('.sticky-add-to-cart__bar').forEach((node) => {
        if (node.style.getPropertyValue('display') !== 'none') node.style.setProperty('display', 'none', 'important');
      });
    };

    // The size guide link belongs right under the size buttons.
    const placeSizeGuide = () => {
      document.querySelectorAll('.lavirena-vivaia-variant-picker').forEach((wrap) => {
        const guide = wrap.querySelector('.lavirena-size-guide-inline');
        const sizeFieldset = [...wrap.querySelectorAll('fieldset.variant-option')]
          .find((fieldset) => [...fieldset.querySelectorAll('input[type="radio"]')].some(isSize));
        if (guide && sizeFieldset && sizeFieldset.nextElementSibling !== guide) sizeFieldset.insertAdjacentElement('afterend', guide);
      });
    };

    const hydrate = async () => {
      const bar = document.querySelector('.elvessia-purchase-bar');
      const box = bar?.querySelector('.elvessia-purchase-bar__summary');
      if (!bar || !box) return;
      if (!box.querySelector('.lv-fixed-summary__content')) {
        box.innerHTML = `
          <div class="lv-fixed-summary__thumb" aria-hidden="true"><img alt="" loading="eager"></div>
          <div class="lv-fixed-summary__content">
            <div class="lv-fixed-summary__price"></div>
            <div class="lv-fixed-summary__shipping">Free worldwide shipping over $99</div>
          </div>`;
      }
      const data = await loadProduct();
      if (!data) return;
      const variantId = selectedVariantId();
      const variants = data.variants || [];
      const variant = variants.find((item) => String(item.id) === variantId) || variants.find((item) => item.available) || variants[0];
      const price = box.querySelector('.lv-fixed-summary__price');
      const priceText = formatMoney(variant?.price ?? data.price);
      if (price && priceText && price.textContent !== priceText) price.textContent = priceText;
      const img = box.querySelector('.lv-fixed-summary__thumb img');
      const src = productImage(data, variant);
      if (img && src && img.getAttribute('src') !== src) img.setAttribute('src', src);
    };

    const run = () => {
      hideThemeBars();
      placeSizeGuide();
      hydrate();
    };
    return { run, schedule: perFrame(run), hydrate };
  })();

  /* ---------- E. mobile extras ---------- */
  const mobileFlow = (() => {
    const buildPaymentRow = (picker) => {
      let row = document.querySelector('.elvessia-mobile-payment-row');
      if (!row) {
        row = document.createElement('div');
        row.className = 'elvessia-mobile-payment-row';
        row.setAttribute('aria-label', 'Secure payment methods');
      }
      const iconsSource = picker?.querySelector('.lv-audrey-payments__icons');
      // Only rebuild when the icons changed, otherwise the observer would keep firing.
      const signature = iconsSource ? iconsSource.innerHTML : '';
      if (row.dataset.lvSignature === signature && row.firstElementChild) return row;
      row.dataset.lvSignature = signature;
      row.innerHTML = '<span class="elvessia-mobile-payment-row__label">Secure payment with</span>';
      if (iconsSource) [...iconsSource.children].forEach((child) => row.appendChild(child.cloneNode(true)));
      return row;
    };

    const buildMobileSn = (picker) => {
      const source = picker?.querySelector('.lv-audrey-sn');
      if (!source?.textContent?.trim()) return null;
      let sn = document.querySelector('.elvessia-mobile-sn');
      if (!sn) {
        sn = document.createElement('div');
        sn.className = 'elvessia-mobile-sn';
      }
      const text = source.textContent.trim();
      if (sn.textContent !== text) sn.textContent = text;
      return sn;
    };

    const run = () => {
      if (!isMobile()) return;
      const main = document.querySelector("main[data-template='product'], main[data-template^='product.']");
      const picker = main?.querySelector('.lavirena-vivaia-variant-picker');
      const info = main?.querySelector('.lv-vivaia-info');
      if (!main || !picker || !info) return;

      const guide = picker.querySelector('.lavirena-size-guide-inline');
      if (guide && guide.style.getPropertyValue('display') !== 'block') {
        guide.hidden = false;
        guide.removeAttribute('aria-hidden');
        guide.style.setProperty('display', 'block', 'important');
        guide.style.setProperty('visibility', 'visible', 'important');
        guide.style.setProperty('opacity', '1', 'important');
      }

      // Payment methods straight after the colour / size picker.
      const payment = buildPaymentRow(picker);
      if (picker.nextElementSibling !== payment) picker.insertAdjacentElement('afterend', payment);

      // SN after the details / care accordions.
      const sn = buildMobileSn(picker);
      if (sn && info.lastElementChild !== sn) info.appendChild(sn);
    };
    return { schedule: perFrame(run) };
  })();

  const compareAt = (() => {
    const run = async () => {
      if (!isMobile()) return;
      const main = document.querySelector("main[data-template='product'], main[data-template^='product.']");
      const productPrice = main?.querySelector('.elv-vivaia-mobile-color-price') || main?.querySelector('.product-details product-price');
      const container = productPrice?.querySelector('[ref="priceContainer"]');
      if (!main || !productPrice || !container) return;

      const data = await loadProduct();
      if (!data) return;
      const id = String(
        document.querySelector('.buy-buttons-block form input[name="id"]')?.value
          || document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value
          || document.querySelector('product-form-component form input[name="id"]')?.value
          || ''
      ).trim();
      const variants = Array.isArray(data.variants) ? data.variants : [];
      const variant = variants.find((item) => String(item.id) === id) || variants.find((item) => item.available) || variants[0];
      if (!variant) return;

      const price = Number(variant.price);
      const compare = Number(variant.compare_at_price);
      let compareEl = container.querySelector('.compare-at-price');
      if (Number.isFinite(compare) && Number.isFinite(price) && compare > price) {
        if (!compareEl) {
          compareEl = document.createElement('span');
          compareEl.className = 'price-item__group price-item--regular compare-at-price elvessia-compare-at-fallback';
          const sale = container.querySelector('.price');
          if (sale) sale.insertAdjacentElement('afterend', compareEl);
          else container.appendChild(compareEl);
        }
        const text = formatMoney(compare);
        if (text && compareEl.textContent.trim() !== text) compareEl.textContent = text;
      } else {
        container.querySelector('.elvessia-compare-at-fallback')?.remove();
      }
    };
    return { schedule: perFrame(run) };
  })();

  // Keeps the mobile gallery from jumping in height while images swap.
  const gallery = (() => {
    let gallerySlides = null;
    let galleryObserver = null;
    let lockedHeight = 0;
    let measuredWidth = 0;

    const apply = () => {
      if (!isMobile()) return;
      const component = document.querySelector('media-gallery slideshow-component');
      const slides = component?.querySelector('slideshow-container > slideshow-slides') || component?.querySelector('slideshow-slides');
      if (!component || !slides) return;
      const rect = slides.getBoundingClientRect();
      const width = Math.round(rect.width);
      const height = Math.ceil(rect.height);
      if (!width || !height) return;
      if (measuredWidth && Math.abs(width - measuredWidth) > 32) {
        lockedHeight = 0;
        component.style.removeProperty('--slide-min-height');
      }
      measuredWidth = width;
      lockedHeight = Math.max(lockedHeight, height);
      const value = `${lockedHeight}px`;
      if (component.style.getPropertyValue('--slide-min-height') !== value) component.style.setProperty('--slide-min-height', value);
      if (gallerySlides !== slides) {
        galleryObserver?.disconnect();
        gallerySlides = slides;
        galleryObserver = new ResizeObserver(() => requestAnimationFrame(apply));
        galleryObserver.observe(slides);
      }
    };

    let t1 = 0;
    let t2 = 0;
    const schedule = () => {
      if (!isMobile()) return;
      requestAnimationFrame(() => requestAnimationFrame(apply));
      clearTimeout(t1);
      clearTimeout(t2);
      t1 = setTimeout(apply, 120);
      t2 = setTimeout(apply, 400);
    };
    return { apply, schedule };
  })();

  /* ---------- F. shipping country wording ---------- */
  const shippingCopy = () => {
    const ship = document.querySelector('.lv-vivaia-ship');
    const countryInput = ship?.querySelector('[data-lv-mobile-country-input]');
    if (!ship || !countryInput) return;
    const usButton = ship.querySelector('[data-lv-mobile-country][data-country-code="US"]');
    if (usButton) {
      usButton.dataset.countryName = 'United States';
      const label = usButton.querySelector('span:first-child');
      if (label && label.textContent !== 'United States') label.textContent = 'United States';
    }
    const currentCode = String(countryInput.value || '').trim().toUpperCase();
    const deliveryLines = ship.querySelectorAll('.lv-vivaia-ship__line span');
    if (currentCode === 'US') {
      const currentName = ship.querySelector('[data-lv-mobile-current-country]');
      if (currentName) currentName.textContent = 'United States';
      const confirm = document.querySelector('[data-lv-mobile-ship-confirm]');
      const titleName = confirm?.querySelector('[data-lv-mobile-ship-name]');
      const copyName = confirm?.querySelector('[data-lv-mobile-ship-copy-name]');
      if (titleName) titleName.textContent = 'United States';
      if (copyName) copyName.textContent = 'United States';
      for (const line of deliveryLines) {
        const text = String(line.textContent || '').trim();
        if (text.startsWith('Estimated delivery to ')) {
          const colon = text.indexOf(':');
          const eta = (colon >= 0 ? text.slice(colon + 1).trim() : '7–12 days').replace(/\bDays\b/g, 'days');
          line.textContent = `Estimated delivery to United States: ${eta}`;
        } else if (text.startsWith('Shipping currently unavailable to ')) {
          line.textContent = 'Shipping currently unavailable to United States';
        }
      }
    } else {
      for (const line of deliveryLines) {
        if (line.textContent?.includes(' Days')) line.textContent = line.textContent.replace(/\bDays\b/g, 'days');
      }
    }
  };

  /* ---------- wiring ---------- */
  const scheduleAll = () => {
    sizePicker.schedule();
    purchaseBar.syncSoon();
    summary.schedule();
    mobileFlow.schedule();
    compareAt.schedule();
    gallery.schedule();
  };

  document.addEventListener('change', (event) => {
    sizePicker.schedule();
    const input = event.target.closest?.('variant-picker input[type="radio"]');
    if (!event.target.closest?.('variant-picker')) return;
    setTimeout(summary.hydrate, 60);
    setTimeout(compareAt.schedule, 70);
    if (input && optionName(input).includes('color')) {
      gallery.apply();
      gallery.schedule();
    }
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('variant-picker')) sizePicker.schedule();
  }, true);
  document.addEventListener('shopify:section:load', () => { scheduleAll(); shippingCopy(); });
  document.addEventListener('shopify:section:reorder', scheduleAll);
  if (mq.addEventListener) mq.addEventListener('change', scheduleAll);

  const observer = new MutationObserver((records) => {
    if (records.some((record) => record.type === 'childList')) {
      scheduleAll();
    } else if (records.some(sizePicker.relevant)) {
      sizePicker.schedule();
    }
  });

  const start = () => {
    loadProduct();
    ensureStyles();
    purchaseBar.ensureBar();
    purchaseBar.sync();
    summary.run();
    shippingCopy();
    scheduleAll();
    window.addEventListener('load', gallery.schedule, { once: true });
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['checked', 'disabled', 'aria-disabled', 'data-option-available'],
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
